/**
 * RP-32.4 — Resource Plan comments: line notes, whole-plan comments from the Activity tab,
 * RP-34's Concern replies later (one table, one API — product decision 2026-10-17).
 *
 * Writes invalidate `resourcePlanKeys.all`, which covers the thread and the plan's per-line
 * note counts.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { send, useInvalidatePlans } from "@/hooks/use-resource-plans";
import { planCommentKeys } from "@/lib/query";
import type { PlanCommentsView } from "@/schemas/resource-plans";

/** What a thread is about. `{}` = every comment on the plan. RP-34 adds `{ concernId }`. */
export interface CommentScope {
  planLineId?: string;
}

export function usePlanComments(planId: string, scope: CommentScope, enabled: boolean) {
  return useQuery({
    queryKey: planCommentKeys.list(planId, scope),
    queryFn: () => {
      const params = new URLSearchParams();
      if (scope.planLineId !== undefined) params.set("planLineId", scope.planLineId);
      const query = params.toString();
      return send<PlanCommentsView>(
        `/api/plans/${planId}/comments${query ? `?${query}` : ""}`,
        "GET",
      );
    },
    enabled,
  });
}

export function useAddPlanComment(planId: string) {
  const invalidate = useInvalidatePlans();
  return useMutation({
    /** No `planLineId` = a comment on the whole plan (the Activity tab). */
    mutationFn: (input: { planLineId?: string; body: string }) =>
      send<PlanCommentsView>(`/api/plans/${planId}/comments`, "POST", input),
    onSuccess: (_data, input) => {
      invalidate();
      toast.success(input.planLineId === undefined ? "Comment posted" : "Note added");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to add the note"),
  });
}

export function useDeletePlanComment() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (commentId: string) =>
      send<PlanCommentsView>(`/api/plan-comments/${commentId}`, "DELETE"),
    onSuccess: () => {
      invalidate();
      toast.success("Note removed");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to remove the note"),
  });
}
